import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { HttpService } from '@shared/service/http.service';
import { filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { FormConfigService } from '@shared/configurable/form-config.service';
import { TableConfigService } from '@shared/configurable/table-config.service';
import { AuthApiService } from '@shared/api/auth-api/auth-api.service';
import { LoaderService } from '@shared/service/loader.service';
import { IGroup } from '@shared/api/group-api/models';
import { IUser } from '@shared/models/IUser';
import { PermissionService } from '@shared/service/permission.service';
import { UserStore } from '@shared/store/user.store';
import { PermissionCodes } from '@shared/enums/PermissionCodes';
import { UiConfigStore } from '@shared/store/ui-config.store';
import { StorageService } from '@shared/service/storage.service';
import { STORAGE_KEYS } from '@shared/constants/StorageKeys';
import { InsuredStore } from '@shared/store/insured.store';
import { LocaleService } from '@shared/service/locale.service';
import { TokenStore } from '@shared/store/token.store';
import { MessageService } from '@ui/services/messageservice';
import { LabelsService } from '@private/services/labels.service';
import { OptionsService } from '@private/services/options.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  // region Services
  private readonly _storage = inject(StorageService);
  private readonly _storeUser = inject(UserStore);
  private readonly _storeInsured = inject(InsuredStore);
  private readonly _storeToken = inject(TokenStore);
  private readonly _storeUi = inject(UiConfigStore);
  private readonly _http = inject(HttpService);
  private readonly _permission = inject(PermissionService);
  private readonly _apiAuth = inject(AuthApiService);
  private readonly _loader = inject(LoaderService);
  private readonly _router = inject(Router);
  private readonly _formConfig = inject(FormConfigService);
  private readonly _tableConfig = inject(TableConfigService);
  private readonly _locale = inject(LocaleService);
  private readonly _msgSrv = inject(MessageService);
  private readonly _labels = inject(LabelsService);
  private readonly _options = inject(OptionsService);
  // endregion

  constructor() {
    window.addEventListener('storage', event => {
      if (event.key === this._storage.buildKey(STORAGE_KEYS.USER_STORAGE_KEY)) {
        const parsed = event.newValue ? (JSON.parse(event.newValue) as IUser) : null;
        const currentGroup = this._storeUser.currentGroup();
        if (parsed && currentGroup && parsed.currentGroup && currentGroup.idGroup !== parsed.currentGroup.idGroup) {
          this._storeUser.setCurrentGroupWithoutUpdateStorage(parsed.currentGroup);
          this._router.navigate(['/']);
        }
      }
    });
  }

  updateUser(user: IUser) {
    this._storeUi.setIsDark(user?.userInfo?.useDarkTheme ?? false);
    this._storeUser.setUser(user);
  }

  getAllowedScope(scope = this._storeUser.userInfo()?.defaultSearchScope): number | null {
    const hasBoth = this._permission.checkAccess([PermissionCodes.InsuredFilter, PermissionCodes.InsuredSearch], true);
    if (hasBoth) {
      return scope;
    }
    if (this._permission.checkAccess(PermissionCodes.InsuredFilter)) {
      return 0;
    }
    if (this._permission.checkAccess(PermissionCodes.InsuredSearch)) {
      return 1;
    }
    return null;
  }

  changeGroup(group: IGroup): Observable<void> {
    return this.changeGroupRequest(group).pipe(switchMap(login => this.handleGroupChange(login.data, group)));
  }

  logout(): void {
    this._loader.show();
    if (this._storeToken.refreshToken) {
      this._apiAuth
        .logout(this._storeToken.refreshToken)
        .pipe(finalize(() => this.logoutActions()))
        .subscribe();
    } else {
      this.logoutActions();
    }
  }

  setUser(user: IUser, updateConfigs = true) {
    this._storeToken.updateTokens(user);
    this._permission.setPermissions(user.permissions);
    this._locale.setLocale(user.region);
    if (updateConfigs) {
      this._formConfig.fetchFormConfigs();
      this._tableConfig.fetchTableConfigs();
    }
    this.updateUser(user);
  }

  refreshTokens() {
    return this._apiAuth.refreshToken(this._storeToken.refreshToken).pipe(
      filter(Boolean), // a rejected refresh is turned into null by AuthInterceptor after it logs out
      tap(res => this.setUser(res.data, false))
    );
  }

  logoutActions() {
    const darkMode = this._storage.get<boolean>(STORAGE_KEYS.DARK_MODE);
    localStorage.clear();
    sessionStorage.clear();
    this._storage.set<boolean>(STORAGE_KEYS.DARK_MODE, darkMode);
    this._storeUser.clear();
    this._storeToken.clear();
    this._msgSrv.clear();
    // @ts-ignore
    Chatra('kill');
    this._loader.hide();
    this._router.navigate(['/']);
  }

  private changeGroupRequest(group: IGroup) {
    return this._http.get('auth/changeGroup', {
      idGroup: group.idGroup,
      refreshToken: this._storeToken.refreshToken,
    });
  }

  private handleGroupChange(login: any, group: IGroup) {
    this._storeToken.updateTokens(login);
    this._permission.setPermissions(login.permissions);
    const user = this._storeUser.user();
    this.updateUser({ ...user, ...login, currentGroup: group });

    const client$ =
      group.idLatestClient && group.idLatestClient !== '0'
        ? this._http.get('insured/getDetail', {
            idInsured: group.idLatestClient,
          })
        : null;

    return (client$ ?? new Observable(sub => sub.next(null))).pipe(
      tap(client => {
        if (client) this._storeInsured.setCurrentInsured(client.data);

        this._formConfig.fetchFormConfigs();
        this._tableConfig.fetchTableConfigs();
        combineLatest(this._labels.loadDictionary(), this._options.loadOptions())
          .pipe(finalize(() => this._loader.hide()))
          .subscribe(() => this._router.navigate(['panel/dashboard']));
      }),
      map(() => void 0)
    );
  }
}
